import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MethodChip from './MethodChip';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { hexToPastel } from '../../../stylesSheets';

export default function EndpointOverridesTable({ overrides, onRemoveOverride }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const getPastelColor = (colorHex) => {
    try {
      return hexToPastel(colorHex);
    } catch (e) {
      return colorHex;
    }
  };

  if (!overrides || overrides.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        {t('cws_app.management.custom_overrides', 'Custom Overrides (Granted/Revoked)')}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('cws_app.management.override_type', 'Type')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('cws_app.management.method', 'Method')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('cws_app.management.path', 'Endpoint Path')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {t('cws_app.management.actions', 'Action')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overrides.map((override, index) => {
              const isGrant = override.type === 'grant';
              const typeColor = isGrant ? theme.palette.success.main : theme.palette.error.main;
              const typePastelBg = getPastelColor(typeColor);

              return (
                <TableRow
                  key={`${override.method}-${override.path}`}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={isGrant ? t('cws_app.management.granted', 'Granted') : t('cws_app.management.revoked', 'Revoked')}
                      size="small"
                      sx={{
                        backgroundColor: typePastelBg,
                        color: isGrant ? theme.palette.success.dark : theme.palette.error.dark,
                        fontWeight: 'bold',
                        border: '1px solid',
                        borderColor: typeColor,
                        borderRadius: '4px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <MethodChip method={override.method} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {override.path}
                    </Typography>
                    {override.parameters?.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {override.parameters.map((p) => (
                          <Chip
                            key={p.name}
                            label={`${p.name} (${p.location})`}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 18,
                              backgroundColor: theme.palette.action.selected,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveOverride(override, override.type)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
